import React, { useState, useEffect, useRef } from 'react';
import { Button, UncontrolledTooltip } from 'reactstrap';
import EditableLabel from './EditableLabel';
import autoAnimate from '@formkit/auto-animate';
import FileUploadButton from '../../api/FileUploadButton';
import {
  externalConnectionAvailable,
  fillActiveField,
} from '../../EMR_Integration/react_webInjector.ts';
import { getEnv } from '../../api/DeviceInfo';
import { useMediaQuery } from 'react-responsive';
import { ThreeOptionToggle } from './ActionButtons';

// Component for a nested sub-instruction (e.g., (a:{Instruction: "...", replyType: "..."}), (b:{Instruction: "...", replyType: "..."}), etc.)
// Each item can be expanded/collapsed and edited individually
const SubInstructionItem = ({
  labelKey,
  title,
  value,
  replyType,
  onChangeReplyType,
  onChangeInstruction,
  disabled,
  isSavingCheckbox,
  isSavingInstruction,
}) => {
  const [open, setOpen] = useState(false); // Tracks expand/collapse state

  return (
    <div style={{ marginBottom: '6px' }}>
      {/* Header row with label and skip checkbox */}
      <div
        className="flex-container"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {/* Expand/collapse label (shows arrow + title or fallback Item A/B/...) */}
        <strong
          className="dropdown-label"
          onClick={() => setOpen(!open)}
          style={{ flex: 1, marginRight: '10px' }}
        >
          <div className={`arrow ${open ? 'open' : ''}`}></div>
          <div>
            {title && title.trim().length > 0
              ? title
              : `Item ${labelKey.toUpperCase()}`}
          </div>
        </strong>

        {/* Skip checkbox (toggles replyType between "no_reply" and "overwrite") */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={replyType === 'no_reply'}
              onChange={(e) => {
                // Save operation #2: Checkbox click → updates replyType
                onChangeReplyType(e.target.checked ? 'no_reply' : 'overwrite');
              }}
              disabled={disabled || isSavingCheckbox}
              style={{ margin: 0 }}
            />
            {isSavingCheckbox ? 'Saving...' : 'Skip'}
          </label>
        </div>
      </div>

      {/* Expanded editor (only visible when item is open) */}
      {open && (
        <div id={`sub-instruction-${labelKey}`}>
          <EditableLabel
            text={value}
            onChange={onChangeInstruction}
            forceShowSave
            onSave={async (newVal) => {
              // Save operation #3: Saving edited subsection instruction
              onChangeInstruction(newVal);
            }}
            disabled={disabled}
            isSaving={isSavingInstruction}
          />
        </div>
      )}
    </div>
  );
};

// Reusable reply-type toggle (Skip/Add-On/Overwrite)
const ReplyTypeSelector = ({
  value,
  onChange,
  disabled,
  isSmallScreen = false,
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginRight: isSmallScreen ? '5px' : '10px',
      marginTop: '1px',
    }}
  >
    <ThreeOptionToggle
      value={value || 'overwrite'}
      onChange={onChange}
      options={[
        { label: 'Skip', value: 'no_reply' },
        { label: 'Add-On', value: 'append' },
        { label: 'Overwrite', value: 'overwrite' },
      ]}
      disabled={disabled}
      isSmallScreen={isSmallScreen}
    />
    <span style={{ fontSize: '0.75rem', marginTop: '2px' }}>
      {(value === 'no_reply' && 'Skip') ||
        (value === 'append' && 'Add-On') ||
        'Overwrite'}
    </span>
  </div>
);

export const CollapsibleCore = ({
  id,
  label,
  shownChildren,
  innerChildren,
  onAddClick,
  border,
  deleteButton,
}) => {
  const [show, setShow] = useState(false);
  const parentRef = useRef(null);

  useEffect(() => {
    parentRef.current && autoAnimate(parentRef.current);
  }, [parentRef]);

  const handleAddClick = () => {
    if (!show) setShow(true); // Open the collapsible if it's not already open
    onAddClick(); // Execute the add functionality
  };
  return (
    <div
      className="outlineContainer"
      ref={parentRef}
      id={id}
      style={{
        border: border ? border : 'none',
      }}
    >
      <div
        className="flex-container"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <strong
          className="dropdown-label"
          onClick={() => setShow(!show)}
          style={{ flex: 1, marginRight: '10px' }} // Adjust the margin as needed
        >
          <div className={`arrow ${show ? 'open' : ''}`}></div>
          <div>{label}</div>
        </strong>
        <div style={{ textAlign: 'right' }}>
          {React.cloneElement(shownChildren, { onClick: handleAddClick })}
        </div>
        <div>{deleteButton}</div>
      </div>
      {show && <div>{innerChildren}</div>}
    </div>
  );
};

const Collapsible = ({
  id,
  label,
  content,
  setContent,
  onAddSample,
  onEditSample,
  samples,
  openConfirmationModal,
  comprehend,
  questionKey,
  copyPaste,
  isTemplate,
  instruction,
  setInstruction,
  setStatus,
  conversationID,
  setRefresh,
  configID,
  sectionText,
  children,
  disabled,
  isNoReply = false,
  triggerShow = false,
  isEmrTemplate = false,
  templateReply,
  setTemplateReply,
  showSelector = false,
  isLibraryConfig = false,
  handleViewOnlyClick,
  deleteButton,
  subsections,
  isDev = false,
  onWriteNotesPencilClick,
}) => {
  const [show, setShow] = useState(false);
  const parentRef = useRef(null);
  const [isCX, setIsCX] = useState(!!children); // This is to check if the component is being used in Chrome Extension
  const [showSamples, setShowSamples] = useState(() =>
    isTemplate || isCX ? true : false,
  ); // This is to hide or show the samples for conversation but show for templates
  const isSmallScreen = useMediaQuery({ query: '(max-width: 500px)' });
  const [apiStatus, setApiStatus] = useState('');
  // State to manage subsection and instruction saving status
  const [showSubsections, setShowSubsections] = useState(false);
  const [savingSubsectionReplyType, setSavingSubsectionReplyType] =
    useState(null); // Which subsection replyType is currently being saved
  const [savingSubsectionInstruction, setSavingSubsectionInstruction] =
    useState(null); // Which subsection instruction is currently being saved
  const [savingTopLevel, setSavingTopLevel] = useState(false); // Whether the top-level instruction is being saved
  const topLevelSaveTimeoutRef = useRef(null); // Ref used to debounce top-level saves

  //Get subsection data (Instruction + replyType) for a given title
  const getSubsectionData = (title) => {
    const parsedContent = parseContent(content);
    const subsectionData = parsedContent.SubSections?.[title] || {};
    const result = {
      instruction: subsectionData.Instruction || '',
      replyType: subsectionData.replyType || 'overwrite',
    };
    return result;
  };

  /**
   * Safely parse content into an object with TopLevelInstruction + SubSections
   * Handles cases where SubSections may be a stringified object.
   * Always returns a consistent shape.
   */
  const parseContent = (content) => {
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        // If SubSections is a string, parse it again
        if (typeof parsed.SubSections === 'string') {
          try {
            parsed.SubSections = JSON.parse(parsed.SubSections);
          } catch {
            parsed.SubSections = {};
          }
        }
        return parsed;
      } catch {
        return { TopLevelInstruction: content, SubSections: {} };
      }
    } else if (typeof content === 'object' && content !== null) {
      return content;
    }
    return { TopLevelInstruction: '', SubSections: {} };
  };

  /**
   * Update a subsection (either replyType or Instruction).
   * Optionally shows temporary "saving..." feedback.
   *
   * @param {string} title - Subsection key
   * @param {object} updates - Fields to update (e.g., { replyType: 'no_reply' })
   * @param {boolean} showFeedback - Whether to show saving feedback
   * @param {boolean} isInstructionSave - Distinguish between replyType save vs. instruction save
   */
  const updateSubsection = async (
    title,
    updates,
    showSavingFeedback = false,
    isInstructionSave = false,
  ) => {
    // Show saving feedback only if requested
    if (showSavingFeedback) {
      if (isInstructionSave) {
        setSavingSubsectionInstruction(title);
      } else {
        setSavingSubsectionReplyType(title);
      }
    }

    try {
      const parsedContent = parseContent(content);
      const existingSubsections = parsedContent.SubSections || {};

      // Deep clone subsections to avoid mutations
      const updatedSubsections = JSON.parse(
        JSON.stringify(existingSubsections),
      );

      // Ensure subsection exists
      if (!updatedSubsections[title]) {
        updatedSubsections[title] = {};
      }

      // Apply updates
      updatedSubsections[title] = {
        ...updatedSubsections[title],
        ...updates,
      };

      // Write new content back
      const newContent = JSON.stringify({
        TopLevelInstruction: parsedContent.TopLevelInstruction,
        SubSections: updatedSubsections,
      });

      setContent(newContent);
    } finally {
      // Reset saving feedback after short delay
      if (showSavingFeedback) {
        setTimeout(() => {
          if (isInstructionSave) {
            setSavingSubsectionInstruction(null);
          } else {
            setSavingSubsectionReplyType(null);
          }
        }, 500);
      }
    }
  };

  /**
   * Delayed auto-save for top-level instructions.
   * Waits 1s after user stops typing before saving.
   */
  const delayTopLevelInstructionSave = (newText) => {
    // Clear existing timeout
    if (topLevelSaveTimeoutRef.current) {
      clearTimeout(topLevelSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save
    topLevelSaveTimeoutRef.current = setTimeout(async () => {
      setSavingTopLevel(true);

      try {
        const parsedContent = parseContent(content);
        const hasSubsections =
          parsedContent.SubSections &&
          Object.keys(parsedContent.SubSections).length > 0;

        const newContent = hasSubsections
          ? JSON.stringify({
              TopLevelInstruction: newText,
              SubSections: parsedContent.SubSections,
            })
          : newText; // Just send the plain text if no subsections exist

        setContent(newContent);
      } finally {
        // Hide saving feedback after a short delay
        setTimeout(() => {
          setSavingTopLevel(false);
        }, 500);
      }
    }, 1000); // 1 second delay
  };

  const handleRefreshClick = async () => {
    if (isNoReply) return;
    setApiStatus('pending');
    const labelKey = label;
    if (showSelector) {
      await comprehend(labelKey, (key, status) => {
        if (key === labelKey) setApiStatus(status);
      });
    } else {
      await comprehend(labelKey, null, (key, status) => {
        if (key === labelKey) setApiStatus(status);
      });
    }
  };

  const getButtonStyle = () => {
    switch (apiStatus) {
      case 'success':
        return { backgroundColor: '#85df9d' }; // green
      case 'none':
        return { backgroundColor: '#85df9d' }; // green (treat as success)
      case 'error':
        return { backgroundColor: '#ffbaba' }; // red
      case 'timeout':
        return { backgroundColor: '#ffbaba' }; // red
      default:
        return { backgroundColor: '' };
    }
  };

  const getButtonTitle = () => {
    switch (apiStatus) {
      case 'success':
        return 'Success';
      case 'none':
        return 'Success';
      case 'error':
        return 'Something went wrong, please re-Comprehend';
      case 'timeout':
        return 'This section timed out, please re-Comprehend';
      default:
        return 'Comprehend';
    }
  };

  const isSpinning = apiStatus === 'pending';

  useEffect(() => {
    const handler = (e) => {
      if (e.detail.key === label) {
        setApiStatus(e.detail.status);
      }
    };
    document.addEventListener('updateComprehendStatus', handler);
    return () =>
      document.removeEventListener('updateComprehendStatus', handler);
  }, [label]);

  useEffect(() => {
    parentRef.current && autoAnimate(parentRef.current);
  }, [parentRef]);

  useEffect(() => {
    if (triggerShow) {
      setShow(true);
    }
  }, [triggerShow]);

  useEffect(() => {
    // Close all collapsible sections when disabled prop changes
    setShow(false);
    setShowSamples(isTemplate || isCX ? true : false);
  }, [disabled, isTemplate, isCX]);

  const handleCopyClick = async () => {
    await navigator.clipboard.writeText(content);
  };

  const toggleSamples = () => {
    setShowSamples(!showSamples); // Toggle the state for showing/hiding samples
  };

  /* Logic to handle external paste (Mic Drop) */
  const [externalPasteAvailable, setExternalPasteAvailable] = useState(
    externalConnectionAvailable(),
  );
  const handleExternalPaste = async () => {
    fillActiveField(content);
  };

  // In the electron app we want to show copy paste not transfer
  const [isElectron, setIsElectron] = useState(getEnv() === 'electron');

  // Only apply responsive layout changes in TemplateManager (isTemplate && showSelector && !isLibraryConfig)
  const isTemplateManager = isTemplate && showSelector && !isLibraryConfig;
  const shouldUseResponsiveLayout =
    isSmallScreen && isTemplateManager && isEmrTemplate;

  return (
    <div className="outlineContainer" ref={parentRef} id={id}>
      <div
        className="flex-container"
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: shouldUseResponsiveLayout ? 'wrap' : 'nowrap',
        }}
      >
        {!shouldUseResponsiveLayout && (
          <div className="button-container">
            {comprehend && ( // This condition is for ConversationDetails with Comprehend option
              <>
                {!isCX && samples && (
                  <>
                    {showSelector && (
                      <ReplyTypeSelector
                        value={templateReply[label]}
                        disabled={disabled}
                        onChange={(newValue) =>
                          setTemplateReply(label, newValue)
                        }
                      />
                    )}
                  </>
                )}
              </>
            )}
            {/* Dev-only toggle in Library Config view or EMR Template */}
            {((isLibraryConfig && isDev) || isEmrTemplate) && showSelector && (
              <ReplyTypeSelector
                value={templateReply && templateReply[questionKey]}
                disabled={disabled}
                onChange={(newValue) => setTemplateReply(questionKey, newValue)}
              />
            )}
          </div>
        )}
        <strong
          className="dropdown-label"
          onClick={() => setShow(!show)}
          style={{
            flex: 1,
            marginRight: '10px',
            minWidth: shouldUseResponsiveLayout ? '0' : 'auto',
          }} // Adjust the margin as needed
        >
          <div className={`arrow ${show ? 'open' : ''}`}></div>
          <div
            style={{
              fontSize: '0.85rem',
              wordWrap: 'break-word',
              maxWidth: '100%',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
            title={label}
          >
            {isSmallScreen
              ? label.length > 18
                ? label.slice(0, 15) + '...'
                : label
              : label}
          </div>
        </strong>
        <div
          className="button-container"
          style={{
            display: 'flex',
            flexDirection: shouldUseResponsiveLayout ? 'column' : 'row',
            alignItems: 'center',
            gap: shouldUseResponsiveLayout ? '4px' : '0',
          }}
        >
          {/* Move ReplyTypeSelector to right side on small screens - only in TemplateManager */}
          {shouldUseResponsiveLayout && (
            <>
              {comprehend && ( // This condition is for ConversationDetails with Comprehend option
                <>
                  {!isCX && samples && (
                    <>
                      {showSelector && (
                        <ReplyTypeSelector
                          value={templateReply[label]}
                          disabled={disabled}
                          onChange={(newValue) =>
                            setTemplateReply(label, newValue)
                          }
                        />
                      )}
                    </>
                  )}
                </>
              )}
              {/* Dev-only toggle in Library Config view or EMR Template */}
              {((isLibraryConfig && isDev) || isEmrTemplate) &&
                showSelector && (
                  <ReplyTypeSelector
                    value={templateReply && templateReply[questionKey]}
                    disabled={disabled}
                    onChange={(newValue) =>
                      setTemplateReply(questionKey, newValue)
                    }
                  />
                )}
            </>
          )}
          {comprehend &&
            !isNoReply &&
            !isLibraryConfig && ( // This condition is for ConversationDetails with Comprehend option (hidden for Library Config)
              <>
                <Button
                  size="med"
                  onClick={handleRefreshClick}
                  title={getButtonTitle()}
                  id="refresh"
                  style={getButtonStyle()}
                >
                  <i
                    className={`fa fa-refresh${isSpinning ? ' fa-spin' : ''}`}
                    style={{ fontSize: '18px' }}
                  ></i>
                </Button>
              </>
            )}
          {isTemplate &&
            !disabled &&
            !isLibraryConfig && ( // This condition is for Web App templates
              <>
                <Button
                  id="add-example-button"
                  className="btn"
                  style={{
                    marginBottom: shouldUseResponsiveLayout ? '4px' : '10px',
                  }}
                  size={isSmallScreen ? 'sm' : 'md'}
                  onClick={() => {
                    setShow(true);
                    onAddSample();
                  }}
                  disabled={disabled || samples?.[questionKey]?.length >= 3} // Disable the button
                >
                  Add Example
                </Button>
                {!isEmrTemplate && !disabled && (
                  <Button
                    className="fa fa-trash"
                    style={{
                      marginBottom: shouldUseResponsiveLayout ? '4px' : '10px',
                    }}
                    size={isSmallScreen ? 'sm' : 'md'}
                    onClick={() =>
                      openConfirmationModal('template', questionKey)
                    }
                  />
                )}
                <div className="drag-handle">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </>
            )}
          {isLibraryConfig &&
            deleteButton && ( // This condition is for Library Config templates
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {deleteButton}
              </div>
            )}
          {label === 'Transcript' && (
            <div id="file-upload-button">
              <FileUploadButton
                conversationID={conversationID}
                setStatus={setStatus}
                setRefresh={setRefresh}
              />
            </div>
          )}
          {label === 'Transcript' && onWriteNotesPencilClick && (
            <Button
              size="med"
              title="Additional Visit Information"
              onClick={(e) => {
                e.stopPropagation();
                onWriteNotesPencilClick();
              }}
            >
              <i className="fa fa-pencil" style={{ fontSize: '18px' }}></i>
            </Button>
          )}
          {/* Always show clipboard copy button when copyPaste is enabled */}
          {copyPaste && (
            <Button
              id="copy-text"
              size="med"
              onClick={handleCopyClick}
              title="Copy Text"
            >
              <i className="fa fa-clipboard" style={{ fontSize: '18px' }}></i>
            </Button>
          )}
          {/* Show external paste (transfer) button when available and not transcript/electron */}
          {copyPaste &&
            externalPasteAvailable &&
            label !== 'Transcript' &&
            !isElectron && (
              <Button
                id="copy-text"
                size="med"
                onClick={handleExternalPaste}
                title="Copy Text"
              >
                <i className="fa fa-exchange" style={{ fontSize: '18px' }}></i>
              </Button>
            )}
        </div>
      </div>
      {show && (
        <div>
          {label === 'Transcript' ? (
            <EditableLabel text={content} onChange={setContent} />
          ) : (
            <>
              <div
                style={{ marginBottom: comprehend ? '10px' : '0' }}
                id={
                  isTemplate || instruction === undefined
                    ? 'instructions-input'
                    : 'generated-response-input'
                }
              >
                {isTemplate || instruction === undefined ? (
                  <EditableLabel
                    labelText="Instructions"
                    text={(() => {
                      const parsed = parseContent(content);
                      return parsed.TopLevelInstruction || '';
                    })()}
                    onChange={(newText) => {
                      // Auto-save top-level instruction with delay
                      delayTopLevelInstructionSave(newText);
                    }}
                    configID={configID}
                    sectionText={sectionText}
                    disabled={disabled}
                    section={questionKey}
                    isLibraryConfig={isLibraryConfig}
                    onClick={disabled ? handleViewOnlyClick : undefined}
                    isInstruction={true}
                    showSubsections={showSubsections}
                    onToggleSubsections={() =>
                      setShowSubsections(!showSubsections)
                    }
                    subsections={subsections}
                    setContent={setContent}
                    content={content}
                    isSaving={savingTopLevel}
                  >
                    {showSubsections && subsections && (
                      <div
                        style={{
                          marginTop: '6px',
                          maxHeight: '240px',
                          overflowY: 'auto',
                          padding: 0,
                          background: 'transparent',
                        }}
                      >
                        {Object.entries(subsections).map(
                          ([title, data], idx) => {
                            const subsectionData = getSubsectionData(title);
                            return (
                              <SubInstructionItem
                                labelKey={String.fromCharCode(
                                  'a'.charCodeAt(0) + idx,
                                )}
                                title={title}
                                value={subsectionData.instruction}
                                replyType={subsectionData.replyType}
                                onChangeReplyType={(newReplyType) => {
                                  // Save operation #2: Checkbox click (no saving feedback)
                                  updateSubsection(
                                    title,
                                    { replyType: newReplyType },
                                    false,
                                    false,
                                  );
                                }}
                                onChangeInstruction={(newInstruction) => {
                                  // Save operation #3: Subsection instruction save (with saving feedback)
                                  updateSubsection(
                                    title,
                                    { Instruction: newInstruction },
                                    true,
                                    true,
                                  );
                                }}
                                disabled={disabled}
                                isSavingCheckbox={
                                  savingSubsectionReplyType === title
                                }
                                isSavingInstruction={
                                  savingSubsectionInstruction === title
                                }
                              />
                            );
                          },
                        )}
                      </div>
                    )}
                  </EditableLabel>
                ) : (
                  <EditableLabel
                    labelText="Generated Response"
                    text={(() => {
                      const parsed = parseContent(content);
                      return parsed.TopLevelInstruction || content;
                    })()}
                    onChange={setContent}
                  />
                )}
              </div>
            </>
          )}
          {comprehend && ( // This condition is for ConversationDetails with Comprehend option
            <>
              {!isCX && samples && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                    width: '100%',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    padding: 0,
                    gap: 0, // Ensure no gap between children
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.85rem',
                      whiteSpace: 'nowrap',
                      marginRight: '8px',
                      fontWeight: 'bold',
                    }}
                  >
                    {showSelector ? 'Examples:' : 'Instructions & Examples:'}
                  </div>
                  <Button
                    onClick={() => setShowSamples(true)}
                    size="med"
                    disabled={showSamples}
                    id="show-examples-button"
                    style={{
                      flex: 1,
                      borderRadiusLeft: '10px',
                      margin: 0,
                      padding: 10,
                      backgroundColor: showSamples ? '#007bff' : '#e0e0e0',
                      color: showSamples ? 'white' : '#333',
                      border: 'none',
                    }}
                  >
                    Show
                  </Button>
                  <Button
                    onClick={() => setShowSamples(false)}
                    size="med"
                    disabled={!showSamples}
                    id="hide-examples-button"
                    style={{
                      flex: 1,
                      borderRadiusLeft: '10px',
                      margin: 0,
                      padding: 10,
                      backgroundColor: !showSamples ? '#007bff' : '#e0e0e0',
                      color: !showSamples ? 'white' : '#333',
                      border: 'none',
                    }}
                  >
                    Hide
                  </Button>
                </div>
              )}
            </>
          )}
          {showSamples &&
            comprehend &&
            instruction !== undefined &&
            setInstruction !== undefined && (
              <div id="instructions-input" style={{ marginBottom: '10px' }}>
                <EditableLabel
                  labelText="Instructions"
                  text={instruction}
                  onChange={setInstruction}
                  configID={configID}
                  sectionText={sectionText}
                  disabled={disabled}
                  section={questionKey}
                />
              </div>
            )}
          {!isTemplate && showSamples && (
            <Button
              color="primary"
              style={{ marginBottom: '10px', width: '100%' }}
              size="med"
              onClick={() => {
                setShow(true);
                setShowSamples(true);
                onAddSample();
              }}
              disabled={disabled || samples[questionKey]?.length >= 3} // Disable the button
              title="Add Example"
              id="add-example-button"
            >
              Add Example
            </Button>
          )}
          {samples &&
            showSamples && ( // This condition is for ConversationDetails with Comprehend option
              <div className="samples-container">
                {samples[questionKey] &&
                  samples[questionKey].map((sample, index) => (
                    <div
                      key={`sample-${questionKey}-${index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <div style={{ flexGrow: 1, marginRight: '10px' }}>
                        <EditableLabel
                          labelText={`Example Note #${index + 1}`}
                          text={sample}
                          onChange={(newSampleText) =>
                            onEditSample(index, newSampleText)
                          }
                          disabled={disabled}
                          onClick={disabled ? handleViewOnlyClick : undefined}
                        />
                      </div>
                      {!disabled && !isLibraryConfig && (
                        <Button
                          className="fa fa-trash"
                          style={{ marginBottom: '10px' }}
                          size="med"
                          onClick={() =>
                            openConfirmationModal('sample', questionKey, index)
                          }
                        />
                      )}
                    </div>
                  ))}
              </div>
            )}
          {children}
        </div>
      )}
    </div>
  );
};

export const WriteNotesCollapsible = ({ content, setContent }) => {
  const [show, setShow] = useState(true);
  const parentRef = useRef(null);

  useEffect(() => {
    parentRef.current && autoAnimate(parentRef.current);
  }, [parentRef]);

  return (
    <div className="outlineContainer" ref={parentRef}>
      <div
        className="flex-container"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <strong
          className="dropdown-label"
          onClick={() => setShow(!show)}
          style={{ flex: 1, marginRight: '10px' }}
        >
          <div className={`arrow ${show ? 'open' : ''}`}></div>
          <div style={{ fontSize: '0.85rem' }}>
            Additional Visit Information
          </div>
        </strong>
        <i
          id="write-notes-info-icon"
          className="fa fa-info-circle"
          style={{ fontSize: '13px', color: '#888', marginRight: '8px' }}
        ></i>
        <UncontrolledTooltip target="write-notes-info-icon">
          Any information entered here will be used to write the note.
        </UncontrolledTooltip>
      </div>
      {show && (
        <EditableLabel
          text={content || ''}
          onChange={setContent}
          placeholder="Any information entered here will be used to write the note."
        />
      )}
    </div>
  );
};

export default Collapsible;
